// Root route created by tools/gen_web_routes.py; owned by hand from here on.
import type { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet, useMatches, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppConfigHold } from '../shared/appConfig/AppConfigHold'
import '../shared/shell/strings'

/** What every loader receives: the query cache, so loaders and components read the same data. */
export interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: () => (
    <main data-testid="NotFoundScreen">
      <h1 data-testid="notFound_titleValue">Nothing here</h1>
    </main>
  ),
})

/** The tab title names the screen (`shell.title.<screenId>`), never the id; a route without a screen id keeps *Orca*. */
function ScreenTitle() {
  const { t, i18n } = useTranslation()
  const screenId = useMatches({ select: (matches) => matches.at(-1)?.staticData?.screenId })
  const key = screenId ? `shell.title.${screenId}` : undefined
  const title = key && i18n.exists(key) ? t(key) : undefined
  useEffect(() => {
    document.title = title ? `${title} · Orca` : 'Orca'
  }, [title])
  return <RouteAnnouncer title={title ?? 'Orca'} />
}

/**
 * After a navigation to another path (E23U2), never on the first load or a change of the search alone: a polite live
 * region reads the new screen's name — emptied first and filled a tick later, so a screen with the same name (one task
 * after another) is read again — and the focus moves to the screen's heading, or its `main` without one. The focus
 * stays where it is when it is already inside `main` (back and forward in a split view) or a dialog or a field took it
 * as the screen opened. The heading loses its ring and the `tabindex` it was given when the focus leaves it or at the
 * next navigation; the `main` fallback keeps its ring.
 */
function RouteAnnouncer({ title }: { title: string }) {
  const path = useRouterState({ select: (state) => state.resolvedLocation?.pathname })
  const shown = useRef(path)
  const latestTitle = useRef(title)
  latestTitle.current = title
  const [said, setSaid] = useState('')
  useEffect(() => {
    if (path === undefined || path === shown.current) return
    const first = shown.current === undefined
    shown.current = path
    if (first) return
    for (const marked of document.querySelectorAll<HTMLElement>('[data-route-focus]')) release(marked)
    setSaid('')
    const timer = setTimeout(() => setSaid(latestTitle.current), 100)
    // In the effect, never a frame later: a focus the person or a test gives meanwhile is theirs to keep.
    const held = document.activeElement
    const main = document.querySelector<HTMLElement>('main')
    const inMain = held !== document.body && main?.contains(held) === true
    const taken = held?.closest(
      'dialog, [role="dialog"], [role="alertdialog"], input, textarea, select, [contenteditable]',
    )
    const heading = main?.querySelector<HTMLElement>('h1') ?? main
    if (!inMain && !taken && heading) {
      heading.dataset.routeFocus = heading.hasAttribute('tabindex') ? '' : 'tabindex'
      if (heading.dataset.routeFocus) heading.setAttribute('tabindex', '-1')
      heading.addEventListener('blur', () => release(heading), { once: true })
      heading.focus({ preventScroll: true })
    }
    return () => clearTimeout(timer)
  }, [path])
  return (
    <div aria-live="polite" aria-atomic="true" data-route-announcer className="sr-only">
      {said}
    </div>
  )
}

/** Takes back what the route focus gave an element: its mark and the `tabindex` it did not have. */
function release(element: HTMLElement) {
  if (element.dataset.routeFocus === 'tabindex') element.removeAttribute('tabindex')
  delete element.dataset.routeFocus
}

function Root() {
  const { queryClient } = Route.useRouteContext()
  return (
    <>
      <ScreenTitle />
      <AppConfigHold queryClient={queryClient} />
      <Outlet />
    </>
  )
}
